import { useState, useEffect, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../lib/firebase';
import { useFamily } from '../../contexts/FamilyContext';
import toast from 'react-hot-toast';
import Icon from '../shared/Icon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faLink, faStop } from '@fortawesome/free-solid-svg-icons';

export default function ChatWidget({ currentExperience = null, experiences = [], isOpen: externalIsOpen = null, onClose: externalOnClose = null }) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== null ? externalIsOpen : internalIsOpen;
  const setIsOpen = useCallback((value) => {
    if (externalOnClose) {
      externalOnClose(value);
    } else {
      setInternalIsOpen(value);
    }
  }, [externalOnClose]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [selectedExperience, setSelectedExperience] = useState(currentExperience);
  const [showExperienceDropdown, setShowExperienceDropdown] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const messagesEndRef = useRef(null);
  const experienceDropdownRef = useRef(null);
  const { family } = useFamily();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        try {
          const recognitionInstance = new SpeechRecognition();
          recognitionInstance.continuous = false;
          recognitionInstance.interimResults = false;
          recognitionInstance.lang = 'en-US';

          recognitionInstance.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setInput(prev => prev + (prev ? ' ' : '') + transcript);
            setIsRecording(false);
          };

          recognitionInstance.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            setIsRecording(false);
            if (event.error === 'not-allowed') {
              toast.error('Microphone access denied. Please enable it in your browser settings.');
            } else {
              toast.error('Speech recognition failed. Please try again.');
            }
          };

          recognitionInstance.onend = () => {
            setIsRecording(false);
          };

          setRecognition(recognitionInstance);
        } catch (error) {
          console.error('Error initializing speech recognition:', error);
        }
      }
    }
  }, []);

  // Update selectedExperience when currentExperience changes
  useEffect(() => {
    setSelectedExperience(currentExperience);
  }, [currentExperience]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (experienceDropdownRef.current && !experienceDropdownRef.current.contains(event.target)) {
        setShowExperienceDropdown(false);
      }
    };

    if (showExperienceDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExperienceDropdown]);

  useEffect(() => {
    if (isOpen && conversationId) {
      // Load conversation from Firestore
      const loadConversation = async () => {
        try {
          const convDoc = await getDoc(doc(db, 'conversations', conversationId));
          if (convDoc.exists()) {
            const data = convDoc.data();
            setMessages(data.messages || []);
          }
        } catch (error) {
          console.error('Error loading conversation:', error);
        }
      };
      loadConversation();
    } else if (isOpen && !conversationId) {
      // Reset messages when opening new chat
      setMessages([]);
    }
  }, [isOpen, conversationId]);

  // Listen for openChat custom event
  useEffect(() => {
    const handleOpenChat = (event) => {
      setIsOpen(true);
      if (event.detail?.conversationId) {
        setConversationId(event.detail.conversationId);
      } else {
        setConversationId(null);
        setMessages([]);
      }
    };

    const handleChatInput = (event) => {
      setIsOpen(true);
      if (event.detail?.message) {
        setInput(event.detail.message);
        // If there's a linked experience, update currentExperience prop would be needed
        // For now, we'll just set the input
      }
    };

    window.addEventListener('openChat', handleOpenChat);
    window.addEventListener('chatInput', handleChatInput);
    return () => {
      window.removeEventListener('openChat', handleOpenChat);
      window.removeEventListener('chatInput', handleChatInput);
    };
  }, [setIsOpen]);

  const handleStartRecording = () => {
    if (!recognition) {
      toast.error('Speech recognition is not supported in your browser.');
      return;
    }

    try {
      recognition.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recognition:', error);
      toast.error('Failed to start recording. Please try again.');
    }
  };

  const handleStopRecording = () => {
    if (recognition && isRecording) {
      recognition.stop();
      setIsRecording(false);
    }
  };

  const handleLinkExperience = (experience) => {
    setSelectedExperience(experience);
    setShowExperienceDropdown(false);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const chatWithClaude = httpsCallable(functions, 'chatWithClaude');

      const familyContext = {
        familyId: family?.id || null,
        name: family?.name || 'Your Family',
        kids: family?.kids || [],
        onboarding: family?.onboarding || {},
      };

      const experienceContext = selectedExperience
        ? {
            id: selectedExperience.id || null,
            type: selectedExperience.type || null,
            title: selectedExperience.title || null,
            currentPhase: selectedExperience.currentPhase || null,
          }
        : null;

      const result = await chatWithClaude({
        conversationHistory: updatedMessages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
        familyContext,
        currentProject: experienceContext,
        conversationId,
      });

      const assistantMessage = {
        role: 'assistant',
        content: result.data.message,
        timestamp: new Date(),
      };

      setMessages([...updatedMessages, assistantMessage]);
      setConversationId(result.data.conversationId);
    } catch (error) {
      // Log full error for debugging
      console.error('Error sending message:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error details:', error.details);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      
      // Show more specific error messages
      let errorMessage = 'Failed to send message. Please try again.';
      let showDetails = false;
      
      if (error.code === 'functions/not-found') {
        errorMessage = 'Chat function not found. The function may not be deployed.';
        showDetails = true;
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'Permission denied. Please check your authentication.';
        showDetails = true;
      } else if (error.code === 'functions/unavailable') {
        errorMessage = 'Chat service is temporarily unavailable. Please try again later.';
      } else if (error.code === 'functions/deadline-exceeded') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.code === 'functions/internal') {
        errorMessage = 'Internal server error. The function may need to be redeployed.';
        showDetails = true;
      } else if (error.message?.includes('ANTHROPIC_API_KEY') || error.message?.includes('API key') || error.message?.includes('authentication') || error.message?.includes('authentication failed')) {
        errorMessage = 'AI service authentication failed. The API key may be invalid or expired. Please check the API key secret.';
        showDetails = true;
      } else if (error.message) {
        // Show the actual error message if available
        errorMessage = `Error: ${error.message}`;
        showDetails = true;
      }
      
      // Show error with details in console
      if (showDetails) {
        console.error('💡 Troubleshooting:', {
          code: error.code,
          message: error.message,
          suggestion: 'Check browser console for details. Ensure the function is deployed and the API key secret is set.',
        });
      }
      
      toast.error(errorMessage);
      // Remove the user message on error
      setMessages(messages);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 w-14 h-14 sm:w-16 sm:h-16 bg-primary text-white rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center text-2xl sm:text-3xl z-30"
          aria-label="Open chat"
        >
          <Icon name="💬" />
        </button>
      )}

      {/* Chat Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={(e) => {
            // Close modal when clicking backdrop
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div 
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:w-[500px] sm:max-w-[90vw] h-[600px] sm:h-[700px] flex flex-col mb-0 md:mb-0"
            style={{ maxHeight: 'calc(100vh - 64px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-border">
              <div>
                <h3 className="font-serif text-lg sm:text-xl font-bold text-text-primary">
                  Chat Helper
                </h3>
                <p className="text-xs sm:text-sm text-text-secondary">
                  Ask me anything about planning or traditions
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-2xl text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Close chat"
              >
                ×
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
              {messages.length === 0 && (
                <div className="text-center text-text-secondary text-sm py-8">
                  <div className="mb-3"><Icon name="👋" size="2x" className="text-text-secondary" /></div>
                  <p>Hi! I'm here to help with your Jewish family planning.</p>
                  <p className="mt-2">Ask me about recipes, activities, traditions, or your experience!</p>
                </div>
              )}
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] sm:max-w-[75%] rounded-2xl p-3 sm:p-4 ${
                      message.role === 'user'
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-primary'
                    }`}
                  >
                    <div className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                      {message.content}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-surface rounded-2xl p-3 sm:p-4">
                    <div className="flex items-center gap-2 text-text-secondary">
                      <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 sm:p-5 border-t border-border">
              {/* Link to Experience */}
              {experiences.length > 0 && (
                <div className="mb-3 relative" ref={experienceDropdownRef}>
                  <button
                    onClick={() => setShowExperienceDropdown(!showExperienceDropdown)}
                    className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center gap-2 ${
                      selectedExperience
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary hover:bg-surface-dark'
                    }`}
                  >
                    <FontAwesomeIcon icon={faLink} className="text-xs" />
                    <span>
                      {selectedExperience ? selectedExperience.title : 'Link to Experience'}
                    </span>
                    {selectedExperience && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedExperience(null);
                        }}
                        className="ml-1 hover:bg-white/20 rounded px-1"
                      >
                        ×
                      </button>
                    )}
                  </button>

                  {showExperienceDropdown && (
                    <div className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-card shadow-lg border border-border z-50 max-h-60 overflow-y-auto">
                      <div className="p-2">
                        <div className="text-xs font-semibold text-text-secondary px-2 py-1 mb-1">
                          Link to Experience
                        </div>
                        {experiences.map((exp) => (
                          <button
                            key={exp.id}
                            onClick={() => handleLinkExperience(exp)}
                            className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface transition-colors text-sm text-text-primary"
                          >
                            {exp.title || 'Untitled Experience'}
                          </button>
                        ))}
                        <button
                          onClick={() => {
                            setSelectedExperience(null);
                            setShowExperienceDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface transition-colors text-sm text-text-secondary"
                        >
                          No link
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary transition-all resize-none text-base"
                  rows="2"
                  disabled={loading || isRecording}
                />
                {/* Audio Button */}
                {recognition && (
                  <button
                    onClick={isRecording ? handleStopRecording : handleStartRecording}
                    disabled={loading}
                    className={`px-3 sm:px-4 py-3 rounded-xl font-semibold transition-all flex-shrink-0 ${
                      isRecording
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-surface text-text-primary hover:bg-surface-dark'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={isRecording ? 'Stop recording' : 'Start voice input'}
                  >
                    <FontAwesomeIcon 
                      icon={isRecording ? faStop : faMicrophone} 
                      className={isRecording ? 'animate-pulse' : ''}
                    />
                  </button>
                )}
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="px-5 sm:px-6 py-3 rounded-xl font-semibold bg-primary text-white hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

